import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// TREE STRUCTURE  (matches the architecture exactly)
// ─────────────────────────────────────────────────────────────────────────────
const TREE = [
  { key: 'general',       label: 'General',        icon: '⚙️' },
  { key: 'organization',  label: 'Organization',   icon: '🏢' },
  {
    key: 'patrol', label: 'Patrol', icon: '🚶', children: [
      { key: 'patrol_rules',  label: 'Patrol Rules',   icon: '📋' },
      { key: 'checkpoints',   label: 'Checkpoints',    icon: '📍' },
      { key: 'gps',           label: 'GPS & Tracking', icon: '📡' },
    ]
  },
  {
    key: 'guards', label: 'Guards', icon: '👮', children: [
      { key: 'attendance',    label: 'Attendance',     icon: '🕐' },
      { key: 'shifts',        label: 'Shifts',         icon: '📅' },
      { key: 'device_rules',  label: 'Device Rules',   icon: '📱' },
    ]
  },
  {
    key: 'alerts', label: 'Alerts', icon: '🔔', children: [
      { key: 'alert_rules',   label: 'Alert Rules',    icon: '⚠️' },
      { key: 'escalation',    label: 'Escalation',     icon: '🚨' },
    ]
  },
  { key: 'incidents',      label: 'Incidents',      icon: '🛑' },
  { key: 'geofencing',     label: 'Geofencing',     icon: '🛡️' },
  { key: 'notifications',  label: 'Notifications',  icon: '📲' },
  { key: 'reports',        label: 'Reports',        icon: '📊' },
  { key: 'integrations',   label: 'Integrations',   icon: '🔌' },
  { key: 'security',       label: 'Security',       icon: '🔐' },
  { key: 'data_retention', label: 'Data Retention', icon: '💾' },
  { key: 'audit_log',      label: 'Audit Log',      icon: '📜' },
];

// Select options per key
const SELECT_OPTIONS = {
  timezone:              ['Asia/Kolkata','Asia/Dubai','Asia/Singapore','Asia/Tokyo','Europe/London','Europe/Paris','America/New_York','America/Los_Angeles','UTC'],
  date_format:           ['DD/MM/YYYY','MM/DD/YYYY','YYYY-MM-DD','D MMM YYYY'],
  time_format:           ['24h','12h'],
  language:              [{ v:'en',l:'English' },{ v:'hi',l:'Hindi' },{ v:'ar',l:'Arabic' }],
  currency:              ['INR','USD','EUR','AED','GBP','SGD'],
  site_hierarchy:        ['flat','hierarchical'],
  missed_checkpoint_action: ['alert','log','ignore'],
  checkpoint_type:       ['gps','qr','nfc','manual'],
  attendance_method:     ['geofence','qr','manual','biometric'],
  report_default_format: ['pdf','excel','csv'],
  compliance_standard:   ['none','ISO 27001','SOC2','GDPR','Custom'],
};

// ─────────────────────────────────────────────────────────────────────────────
// Input component
// ─────────────────────────────────────────────────────────────────────────────
function SettingInput({ k, meta, value, onChange, disabled }) {
  const base = {
    padding: '7px 12px', borderRadius: 6, border: '1px solid #ddd',
    fontSize: 13, background: disabled ? '#f9f9f9' : 'white',
  };

  if (meta.type === 'boolean') {
    const on = value === 'true';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          onClick={() => !disabled && onChange(k, on ? 'false' : 'true')}
          style={{
            width: 44, height: 24, borderRadius: 12, flexShrink: 0,
            background: on ? '#4361ee' : '#d1d5db',
            position: 'relative', transition: 'background .2s',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <div style={{
            width: 18, height: 18, borderRadius: '50%', background: 'white',
            position: 'absolute', top: 3, left: on ? 23 : 3,
            transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
          }} />
        </div>
        <span style={{ fontSize: 12, color: on ? '#4361ee' : '#9ca3af', fontWeight: 500 }}>
          {on ? 'Enabled' : 'Disabled'}
        </span>
      </div>
    );
  }

  if (meta.type === 'select') {
    const opts = SELECT_OPTIONS[k] || [];
    return (
      <select value={value} onChange={e => onChange(k, e.target.value)} disabled={disabled}
        style={{ ...base, minWidth: 160, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        {opts.map(o =>
          typeof o === 'string'
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.v} value={o.v}>{o.l}</option>
        )}
      </select>
    );
  }

  if (meta.type === 'number') {
    return (
      <input type="number" value={value} min={0} disabled={disabled}
        onChange={e => onChange(k, e.target.value)}
        style={{ ...base, width: 100, textAlign: 'right' }} />
    );
  }

  if (meta.type === 'email') {
    return (
      <input type="email" value={value} placeholder="email@example.com" disabled={disabled}
        onChange={e => onChange(k, e.target.value)}
        style={{ ...base, width: 240 }} />
    );
  }

  if (meta.type === 'password') {
    return (
      <input type="password" value={value} placeholder="••••••••" disabled={disabled}
        onChange={e => onChange(k, e.target.value)}
        style={{ ...base, width: 220 }} />
    );
  }

  // text (default) — with logo preview for logo_url
  if (k === 'logo_url') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        <input type="text" value={value} disabled={disabled}
          onChange={e => onChange(k, e.target.value)}
          placeholder="https://example.com/logo.png"
          style={{ ...base, width: 280 }} />
        {value && (
          <img src={value} alt="Logo preview"
            onError={e => { e.target.style.display = 'none'; }}
            style={{
              height: 48, maxWidth: 180, objectFit: 'contain',
              borderRadius: 8, border: '1px solid #e5e7eb',
              padding: 4, background: 'white',
            }} />
        )}
      </div>
    );
  }

  return (
    <input type="text" value={value} disabled={disabled}
      onChange={e => onChange(k, e.target.value)}
      style={{ ...base, width: 220 }} />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Settings panel for one category
// ─────────────────────────────────────────────────────────────────────────────
function Panel({ node, settings, localValues, onChange, disabled }) {
  const entries = Object.entries(settings[node.key] || {});
  if (entries.length === 0) return (
    <div className="card" style={{ color: '#9ca3af', fontSize: 14 }}>
      No settings configured for this section yet.
    </div>
  );

  return (
    <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
      {/* Panel header */}
      <div style={{ padding: '14px 22px', background: '#f8f9fa', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 20 }}>{node.icon}</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>{node.label}</span>
        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 4 }}>{entries.length} settings</span>
      </div>

      {/* Rows */}
      {entries.map(([key, meta], i) => (
        <div key={key} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 22px', gap: 16,
          borderBottom: i < entries.length - 1 ? '1px solid #f3f4f6' : 'none',
          background: i % 2 === 0 ? 'white' : '#fafafa',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a2e' }}>{meta.label}</div>
            <div style={{ fontSize: 11, color: '#c4c9d4', marginTop: 1, fontFamily: 'monospace' }}>{key}</div>
          </div>
          <SettingInput
            k={key} meta={meta}
            value={localValues[key] ?? meta.value}
            onChange={onChange}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tree sidebar
// ─────────────────────────────────────────────────────────────────────────────
function TreeNav({ tree, activeKey, onSelect, settings }) {
  const [expanded, setExpanded] = useState(() => {
    // Auto-expand parent if active child is under it
    const exp = {};
    for (const node of tree) {
      if (node.children) {
        if (node.children.some(c => c.key === activeKey)) exp[node.key] = true;
      }
    }
    return exp;
  });

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const countFor = (key) => Object.keys(settings[key] || {}).length;

  const itemStyle = (key, isChild = false) => ({
    width: '100%', padding: isChild ? '9px 16px 9px 36px' : '11px 16px',
    border: 'none', textAlign: 'left', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 8,
    background: activeKey === key ? '#eef2ff' : 'transparent',
    color: activeKey === key ? '#4361ee' : '#374151',
    fontWeight: activeKey === key ? 600 : 400,
    fontSize: isChild ? 13 : 14,
    borderRight: activeKey === key ? '3px solid #4361ee' : '3px solid transparent',
    transition: 'all .12s',
  });

  return (
    <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.05)', position: 'sticky', top: 16 }}>
      {tree.map((node, idx) => (
        <React.Fragment key={node.key}>
          {/* Divider before Data Retention */}
          {node.key === 'data_retention' && (
            <div style={{ height: 1, background: '#f0f0f0', margin: '4px 0' }} />
          )}

          {node.children ? (
            <>
              {/* Parent row */}
              <button
                onClick={() => toggle(node.key)}
                style={{
                  ...itemStyle(node.key),
                  justifyContent: 'space-between',
                  background: node.children.some(c => c.key === activeKey) ? '#f5f7ff' : 'transparent',
                  color: node.children.some(c => c.key === activeKey) ? '#4361ee' : '#374151',
                  borderRight: '3px solid transparent',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{node.icon}</span>
                  {node.label}
                </span>
                <span style={{ fontSize: 10, color: '#9ca3af', transform: expanded[node.key] ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
              </button>

              {/* Children */}
              {expanded[node.key] && node.children.map(child => (
                <button key={child.key} onClick={() => onSelect(child.key)} style={itemStyle(child.key, true)}>
                  <span style={{ fontSize: 14 }}>{child.icon}</span>
                  <span style={{ flex: 1 }}>{child.label}</span>
                  {countFor(child.key) > 0 && (
                    <span style={{
                      fontSize: 10, background: activeKey === child.key ? '#4361ee' : '#e5e7eb',
                      color: activeKey === child.key ? 'white' : '#9ca3af',
                      borderRadius: 10, padding: '1px 6px', fontWeight: 600,
                    }}>{countFor(child.key)}</span>
                  )}
                </button>
              ))}
            </>
          ) : (
            <button onClick={() => onSelect(node.key)} style={itemStyle(node.key)}>
              <span style={{ fontSize: 16 }}>{node.icon}</span>
              <span style={{ flex: 1 }}>{node.label}</span>
              {countFor(node.key) > 0 && (
                <span style={{
                  fontSize: 10, background: activeKey === node.key ? '#4361ee' : '#e5e7eb',
                  color: activeKey === node.key ? 'white' : '#9ca3af',
                  borderRadius: 10, padding: '1px 6px', fontWeight: 600,
                }}>{countFor(node.key)}</span>
              )}
            </button>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Settings Page
// ─────────────────────────────────────────────────────────────────────────────
export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings]       = useState({});
  const [localValues, setLocalValues] = useState({});
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [dirty, setDirty]             = useState(false);
  const [activeKey, setActiveKey]     = useState('general');
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    axios.get('/api/settings').then(res => {
      setSettings(res.data);
      const flat = {};
      for (const cat of Object.values(res.data)) {
        for (const [k, v] of Object.entries(cat)) flat[k] = v.value;
      }
      setLocalValues(flat);
      setLoading(false);
    }).catch(() => { toast.error('Failed to load settings'); setLoading(false); });
  }, []);

  const handleChange = useCallback((key, value) => {
    setLocalValues(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  const handleSave = async () => {
    if (!isAdmin) return toast.error('Only admins can save settings');
    setSaving(true);
    try {
      await axios.put('/api/settings', localValues);
      toast.success('Settings saved');
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    const flat = {};
    for (const cat of Object.values(settings)) {
      for (const [k, v] of Object.entries(cat)) flat[k] = v.value;
    }
    setLocalValues(flat);
    setDirty(false);
    toast('Changes discarded');
  };

  // Find node in tree (handles nested)
  const findNode = (key) => {
    for (const n of TREE) {
      if (n.key === key) return n;
      if (n.children) {
        const c = n.children.find(c => c.key === key);
        if (c) return c;
      }
    }
    return { key, label: key, icon: '⚙️' };
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚙️</div>
        <div style={{ color: '#666' }}>Loading settings...</div>
      </div>
    </div>
  );

  const activeNode = findNode(activeKey);

  return (
    <div>
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="page-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="page-title">⚙️ Settings</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
            Application-wide configuration
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>● Unsaved changes</span>}
          {!isAdmin && <span className="badge badge-warning">👁 View only — admin required</span>}
          {isAdmin && (
            <>
              <button onClick={handleDiscard} disabled={!dirty} className="btn"
                style={{ opacity: dirty ? 1 : 0.4 }}>↩ Discard</button>
              <button onClick={handleSave} disabled={saving || !dirty} className="btn btn-primary"
                style={{ opacity: dirty ? 1 : 0.5 }}>
                {saving ? '💾 Saving...' : '💾 Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Body: sidebar + panel ─────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20, alignItems: 'start' }}>

        {/* Sidebar tree */}
        <TreeNav
          tree={TREE}
          activeKey={activeKey}
          onSelect={setActiveKey}
          settings={settings}
        />

        {/* Right panel */}
        <div>
          <Panel
            node={activeNode}
            settings={settings}
            localValues={localValues}
            onChange={handleChange}
            disabled={!isAdmin}
          />

          {/* Sticky save bar */}
          {dirty && isAdmin && (
            <div style={{
              marginTop: 16, padding: '14px 20px', borderRadius: 10,
              background: '#fffbeb', border: '1px solid #fcd34d',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: 13, color: '#92400e', fontWeight: 500 }}>
                ⚠️ You have unsaved changes
              </span>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleDiscard} className="btn">Discard</button>
                <button onClick={handleSave} disabled={saving} className="btn btn-primary">
                  {saving ? 'Saving...' : '💾 Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
